import React, { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RefreshCw, DollarSign, Search, Check, X as XIcon, CheckCircle2, XCircle, Download, FilterX } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { getDLToken, ensureDLToken } from "@/lib/dlToken";
import { useAuth } from "@/context/AuthContext";
import { fmtDateTime } from "@/lib/datetime";

const money = (n) => `$${(Number(n || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Return YYYY-MM-DD in Eastern Time for a given ISO string. Used by the
// date-range filter so admins can slice the table by the ET wall-clock day
// (matches how everything else in the app renders).
const etDateIso = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/New_York",
    year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(d).reduce((a, p) => (a[p.type] = p.value, a), {});
  return `${parts.year}-${parts.month}-${parts.day}`;
};

/**
 * CopaySummary — cross-role page listing every order that carries a copay.
 *
 * Roles:
 *   super_admin / dispatcher → sees every pharmacy; can toggle "Dropped Off"
 *   pharmacy_staff           → sees only their own pharmacy; read-only pill
 */
export default function CopaySummary() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");
  const [pharmacyFilter, setPharmacyFilter] = useState("all");
  const [patientFilter, setPatientFilter] = useState("all");
  const [driverFilter, setDriverFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const canToggle = user?.role === "super_admin" || user?.role === "dispatcher";
  // Filter bar is useful to EVERY role that can view this page — pharmacy
  // staff can now slice their own copay list by patient / driver / date
  // range too. Only the "Dropped off" toggle stays gated to canToggle.
  // (2026-10-01)
  const canFilter = true;

  const load = async () => {
    setBusy(true);
    try {
      const r = await api.get("/copay-summary");
      setRows(r.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => { load(); }, []);

  const toggleDropoff = async (row) => {
    if (!canToggle) return;
    const next = !row.copay_dropped_off;
    try {
      const r = await api.post(`/orders/${row.id}/copay-dropoff`, { dropped_off: next });
      setRows((prev) => prev.map((x) => (
        x.id === row.id
          ? { ...x, copay_dropped_off: next,
              copay_dropped_off_at: r.data?.copay_dropped_off_at || null,
              copay_dropped_off_by: r.data?.copay_dropped_off_by || null }
          : x
      )));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const exportCsv = async () => {
    try {
      await ensureDLToken();
      const t = encodeURIComponent(getDLToken());
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/copay-summary.csv?auth=${t}`;
      window.open(url, "_blank", "noopener");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // Nice hover text for the Dropped Off pill — surfaces the audit trail
  // without a modal. Falls back to a friendly hint when nothing is stored.
  const dropoffTitle = (row) => {
    if (row.copay_dropped_off) {
      const when = row.copay_dropped_off_at ? fmtDateTime(row.copay_dropped_off_at) : "unknown time";
      const who = row.copay_dropped_off_by || "unknown user";
      return `Confirmed ${when} by ${who}${canToggle ? " — click to reverse" : ""}`;
    }
    return canToggle ? "Click to mark cash dropped off at the pharmacy" : "Not yet dropped off — waiting on dispatch to confirm";
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      // Text search across the usual identifying fields.
      if (q) {
        const hit = (
          (r.customer_name || "").toLowerCase().includes(q) ||
          (r.customer_phone || "").toLowerCase().includes(q) ||
          (r.address || "").toLowerCase().includes(q) ||
          (r.pharmacy_name || "").toLowerCase().includes(q) ||
          (r.pharmacy_nickname || "").toLowerCase().includes(q) ||
          (r.driver_name || "").toLowerCase().includes(q) ||
          (r.order_number || "").toLowerCase().includes(q)
        );
        if (!hit) return false;
      }
      // Dropdown filters (super_admin / dispatcher only, but harmless if
      // pharmacy_staff ever sees them — defaults are "all").
      if (pharmacyFilter !== "all" && r.pharmacy_id !== pharmacyFilter) return false;
      if (patientFilter !== "all" && (r.customer_name || "") !== patientFilter) return false;
      if (driverFilter !== "all") {
        if (driverFilter === "__none__") {
          if (r.driver_id) return false;
        } else if (r.driver_id !== driverFilter) return false;
      }
      // Date range on delivered_at, falling back to created_at when the
      // order hasn't been delivered yet (matches CSV/audit expectations).
      if (dateFrom || dateTo) {
        const iso = etDateIso(r.delivered_at || r.created_at);
        if (!iso) return false;
        if (dateFrom && iso < dateFrom) return false;
        if (dateTo && iso > dateTo) return false;
      }
      return true;
    });
  }, [rows, search, pharmacyFilter, patientFilter, driverFilter, dateFrom, dateTo]);

  // Filter option lists — derived once per data change so the dropdowns
  // never show stale (or missing) pharmacies/drivers/patients.
  const pharmacyOpts = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      if (r.pharmacy_id && !map.has(r.pharmacy_id)) {
        map.set(r.pharmacy_id, r.pharmacy_nickname || r.pharmacy_name || r.pharmacy_id);
      }
    }
    return [...map.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [rows]);
  const driverOpts = useMemo(() => {
    const map = new Map();
    let hasUnassigned = false;
    for (const r of rows) {
      if (r.driver_id) {
        if (!map.has(r.driver_id)) map.set(r.driver_id, r.driver_name || r.driver_id);
      } else {
        hasUnassigned = true;
      }
    }
    const list = [...map.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
    if (hasUnassigned) list.push({ id: "__none__", name: "(no driver yet)" });
    return list;
  }, [rows]);
  const patientOpts = useMemo(() => {
    const set = new Set();
    for (const r of rows) if (r.customer_name) set.add(r.customer_name);
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const anyFilterActive = search || pharmacyFilter !== "all" || patientFilter !== "all"
    || driverFilter !== "all" || dateFrom || dateTo;
  const resetFilters = () => {
    setSearch("");
    setPharmacyFilter("all");
    setPatientFilter("all");
    setDriverFilter("all");
    setDateFrom("");
    setDateTo("");
  };

  const totals = useMemo(() => filtered.reduce((acc, r) => {
    acc.copay += Number(r.copay || 0);
    if (r.copay_collected) acc.collected += Number(r.copay_collected_amount || r.copay || 0);
    if (r.copay_dropped_off) acc.dropped += Number(r.copay || 0);
    return acc;
  }, { copay: 0, collected: 0, dropped: 0 }), [filtered]);

  return (
    <div className="p-6 max-w-[1500px] mx-auto space-y-5" data-testid="copay-summary-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
            <DollarSign className="h-3.5 w-3.5" /> Cash-collection reconciliation
          </div>
          <h1 className="text-2xl md:text-3xl font-bold mt-1">Copay Summary</h1>
          <div className="text-sm text-muted-foreground">
            Every order with a copay. {canToggle ? "Toggle Dropped Off once the driver hands cash to the pharmacy." : "Read-only view — dispatch confirms the drop-off."}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="h-3.5 w-3.5 absolute left-2.5 top-2.5 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search patient, phone, address…"
              className="pl-8 h-9 w-72"
              data-testid="copay-search"
            />
          </div>
          <Button variant="outline" size="sm" className="rounded-full" onClick={load} data-testid="copay-refresh">
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${busy ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Button size="sm" className="rounded-full" onClick={exportCsv} data-testid="copay-export-csv">
            <Download className="h-3.5 w-3.5 mr-1.5" /> CSV
          </Button>
        </div>
      </div>

      {/* Filters — Patient / Driver / Date range for every role. The
          Pharmacy dropdown is hidden for pharmacy_staff (one pharmacy =
          nothing to pick) so the bar matches the 4-field layout they
          requested. */}
      {canFilter && (
        <Card className="p-3 rounded-xl" data-testid="copay-filters">
          <div className="flex flex-wrap items-end gap-3">
            {user?.role !== "pharmacy_staff" && (
              <div className="flex flex-col gap-1 min-w-[180px]">
                <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Pharmacy</label>
                <Select value={pharmacyFilter} onValueChange={setPharmacyFilter}>
                  <SelectTrigger className="h-9 rounded-full" data-testid="copay-filter-pharmacy"><SelectValue placeholder="All pharmacies" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All pharmacies</SelectItem>
                    {pharmacyOpts.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex flex-col gap-1 min-w-[220px]">
              <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Patient</label>
              <Select value={patientFilter} onValueChange={setPatientFilter}>
                <SelectTrigger className="h-9 rounded-full" data-testid="copay-filter-patient"><SelectValue placeholder="All patients" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="all">All patients</SelectItem>
                  {patientOpts.map((n) => (
                    <SelectItem key={n} value={n}>{n}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1 min-w-[180px]">
              <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Driver</label>
              <Select value={driverFilter} onValueChange={setDriverFilter}>
                <SelectTrigger className="h-9 rounded-full" data-testid="copay-filter-driver"><SelectValue placeholder="All drivers" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All drivers</SelectItem>
                  {driverOpts.map((d) => (
                    <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Date from</label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="h-9 rounded-full w-[160px]"
                data-testid="copay-filter-date-from"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Date to</label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="h-9 rounded-full w-[160px]"
                data-testid="copay-filter-date-to"
              />
            </div>
            {anyFilterActive && (
              <Button variant="ghost" size="sm" className="rounded-full text-muted-foreground" onClick={resetFilters} data-testid="copay-filter-reset">
                <FilterX className="h-3.5 w-3.5 mr-1.5" /> Reset
              </Button>
            )}
          </div>
        </Card>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Card className="p-3 rounded-xl" data-testid="copay-kpi-total">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Total copay</div>
          <div className="text-xl font-bold font-mono-num mt-0.5">{money(totals.copay)}</div>
          <div className="text-[11px] text-muted-foreground">{filtered.length} order(s)</div>
        </Card>
        <Card className="p-3 rounded-xl border-emerald-300 bg-emerald-50 dark:bg-emerald-900/20" data-testid="copay-kpi-collected">
          <div className="text-[10px] uppercase tracking-widest text-emerald-700">Collected by driver</div>
          <div className="text-xl font-bold font-mono-num mt-0.5">{money(totals.collected)}</div>
          <div className="text-[11px] text-muted-foreground">{filtered.filter((r) => r.copay_collected).length} confirmed</div>
        </Card>
        <Card className="p-3 rounded-xl border-blue-300 bg-blue-50 dark:bg-blue-900/20" data-testid="copay-kpi-dropped">
          <div className="text-[10px] uppercase tracking-widest text-blue-700">Dropped off to pharmacy</div>
          <div className="text-xl font-bold font-mono-num mt-0.5">{money(totals.dropped)}</div>
          <div className="text-[11px] text-muted-foreground">{filtered.filter((r) => r.copay_dropped_off).length} confirmed</div>
        </Card>
        <Card className="p-3 rounded-xl border-amber-300 bg-amber-50 dark:bg-amber-900/20" data-testid="copay-kpi-outstanding">
          <div className="text-[10px] uppercase tracking-widest text-amber-700">Outstanding</div>
          <div className="text-xl font-bold font-mono-num mt-0.5">{money(totals.collected - totals.dropped)}</div>
          <div className="text-[11px] text-muted-foreground">Collected but not yet dropped off</div>
        </Card>
      </div>

      {/* Table */}
      <Card className="rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="copay-summary-table">
            <thead className="bg-secondary/40 text-[11px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-3 py-2 font-semibold">Pharmacy</th>
                <th className="text-left px-3 py-2 font-semibold">Patient</th>
                <th className="text-left px-3 py-2 font-semibold">Address</th>
                <th className="text-left px-3 py-2 font-semibold w-32">Phone</th>
                <th className="text-right px-3 py-2 font-semibold w-24">Copay</th>
                <th className="text-center px-3 py-2 font-semibold w-36">Collected<br /><span className="text-[9px] normal-case tracking-normal text-muted-foreground">by driver</span></th>
                <th className="text-center px-3 py-2 font-semibold w-36">Dropped off<br /><span className="text-[9px] normal-case tracking-normal text-muted-foreground">to pharmacy</span></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length ? filtered.map((r) => (
                <tr key={r.id} className="border-t border-border" data-testid={`copay-row-${r.id}`}>
                  <td className="px-3 py-2">
                    <div className="font-medium">{r.pharmacy_nickname || r.pharmacy_name || "—"}</div>
                    {r.pharmacy_nickname && r.pharmacy_name && r.pharmacy_nickname !== r.pharmacy_name && (
                      <div className="text-[10px] text-muted-foreground">{r.pharmacy_name}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 font-medium">{r.customer_name}</td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">{r.address}</td>
                  <td className="px-3 py-2 font-mono-num text-xs">{r.customer_phone || "—"}</td>
                  <td className="px-3 py-2 text-right font-mono-num font-semibold">{money(r.copay)}</td>
                  <td className="px-3 py-2 text-center">
                    {r.copay_collected ? (
                      <div className="flex flex-col items-center gap-0.5">
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300" data-testid={`copay-collected-yes-${r.id}`}>
                          <CheckCircle2 className="h-3 w-3" /> Yes
                        </span>
                        {r.delivered_at && (
                          <span className="text-[9px] text-muted-foreground font-mono-num whitespace-nowrap" title={`Delivered ${fmtDateTime(r.delivered_at)}`}>
                            {fmtDateTime(r.delivered_at)}
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-rose-100 text-rose-800 border border-rose-300" data-testid={`copay-collected-no-${r.id}`}>
                        <XCircle className="h-3 w-3" /> No
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {canToggle ? (
                      <div className="flex flex-col items-center gap-0.5">
                        <button
                          type="button"
                          onClick={() => toggleDropoff(r)}
                          className={`inline-flex items-center gap-1.5 rounded-full text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1 border transition-colors ${
                            r.copay_dropped_off
                              ? "bg-blue-100 text-blue-800 border-blue-300 hover:bg-blue-200"
                              : "bg-secondary text-muted-foreground border-border hover:bg-blue-50 hover:text-blue-800 hover:border-blue-300"
                          }`}
                          title={dropoffTitle(r)}
                          data-testid={`copay-dropoff-toggle-${r.id}`}
                          aria-pressed={!!r.copay_dropped_off}
                        >
                          {r.copay_dropped_off ? <><Check className="h-3 w-3" /> Yes</> : <><XIcon className="h-3 w-3" /> No</>}
                        </button>
                        {r.copay_dropped_off && r.copay_dropped_off_at && (
                          <span className="text-[9px] text-muted-foreground font-mono-num whitespace-nowrap" title={dropoffTitle(r)}>
                            {fmtDateTime(r.copay_dropped_off_at)}
                          </span>
                        )}
                      </div>
                    ) : (
                      r.copay_dropped_off ? (
                        <div className="flex flex-col items-center gap-0.5">
                          <span
                            className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 border border-blue-300 cursor-help"
                            title={dropoffTitle(r)}
                            data-testid={`copay-dropoff-yes-${r.id}`}
                          >
                            <Check className="h-3 w-3" /> Yes
                          </span>
                          {r.copay_dropped_off_at && (
                            <span className="text-[9px] text-muted-foreground font-mono-num whitespace-nowrap" title={dropoffTitle(r)}>
                              {fmtDateTime(r.copay_dropped_off_at)}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span
                          className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-secondary text-muted-foreground border border-border cursor-help"
                          title={dropoffTitle(r)}
                          data-testid={`copay-dropoff-no-${r.id}`}
                        >
                          <XIcon className="h-3 w-3" /> No
                        </span>
                      )
                    )}
                  </td>
                </tr>
              )) : (
                <tr><td colSpan={7} className="text-center py-8 text-muted-foreground text-sm">
                  {busy ? "Loading…" : "No orders with a copay right now."}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
